/**
 * Protocol-level promotion: the protocols with their rollups, one protocol's types, generating templates
 * for its ready types, and promoting it: each scenario's reply and writes, applied at once.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type {
	AppliedProtocol,
	PromotionJob,
	PromotionPlan,
	PromotionPreview,
	PromotionRecord,
	PromotionSettings,
	PromotionSettingsChange,
	ProtocolDetail,
	ProtocolList,
	ScenarioSetting
} from '@/modules/promotion/types';

export interface ListProtocolsParams {
	search?: string;
	page?: number;
	limit?: number;
	bu_id?: string;
}

export const promotionService = {
	async listProtocols(params: ListProtocolsParams): Promise<ProtocolList> {
		const response = await api.get<ApiResponse<ProtocolList>>(API_ENDPOINTS.PROMOTION.PROTOCOLS, { params });
		return response.data.data;
	},

	async getProtocol(sopId: string): Promise<ProtocolDetail> {
		const response = await api.get<ApiResponse<ProtocolDetail>>(API_ENDPOINTS.PROMOTION.PROTOCOL(sopId));
		return response.data.data;
	},

	/** Queues template generation for the protocol's ready types, or returns the job already running. */
	async generate(
		sopId: string,
		body: { intents?: string[]; typeIds?: string[]; source?: 'approved' }
	): Promise<PromotionJob> {
		const response = await api.post<ApiResponse<{ job: PromotionJob }>>(API_ENDPOINTS.PROMOTION.GENERATE(sopId), body);
		return response.data.data.job;
	},

	async getJob(sopId: string): Promise<PromotionJob | null> {
		const response = await api.get<ApiResponse<{ job: PromotionJob | null }>>(API_ENDPOINTS.PROMOTION.JOB(sopId));
		return response.data.data.job;
	},

	async apply(sopId: string): Promise<AppliedProtocol> {
		const response = await api.post<ApiResponse<AppliedProtocol>>(API_ENDPOINTS.PROMOTION.APPLY(sopId));
		return response.data.data;
	},

	/** Each scenario's reply and writes with their gates and evidence, the check, and the newest promotion. */
	async getPlan(sopId: string): Promise<PromotionPlan> {
		const response = await api.get<ApiResponse<PromotionPlan>>(API_ENDPOINTS.PROMOTION.PLAN(sopId));
		return response.data.data;
	},

	/**
	 * What promoting with these settings (or stopping, with null) would change on each blueprint, and what it
	 * switches on before Ready. `drafts`: the waiting drafts it adds, every one when absent. Writes nothing.
	 */
	async preview(
		sopId: string,
		scenarios: Record<string, ScenarioSetting> | null,
		drafts?: string[]
	): Promise<PromotionPreview> {
		const response = await api.post<ApiResponse<PromotionPreview>>(API_ENDPOINTS.PROMOTION.PREVIEW(sopId), {
			scenarios,
			...(drafts ? { drafts } : {})
		});
		return response.data.data;
	},

	/**
	 * `drafts`: the waiting drafts to add (some or all), each its type's latest or Promote refuses.
	 * `acknowledgeNotReady`: the not-Ready replies (type id) and writes (`typeId:tool`) the person confirmed.
	 */
	async promote(
		sopId: string,
		scenarios: Record<string, ScenarioSetting>,
		drafts: string[],
		acknowledgeNotReady: string[] = []
	): Promise<PromotionRecord> {
		const response = await api.post<ApiResponse<PromotionRecord>>(API_ENDPOINTS.PROMOTION.PROMOTE(sopId), {
			scenarios,
			drafts,
			acknowledgeNotReady
		});
		return response.data.data;
	},

	/** The protocol's promotions, newest first, each with its events. */
	async history(sopId: string): Promise<PromotionRecord[]> {
		const response = await api.get<ApiResponse<{ promotions: PromotionRecord[] }>>(API_ENDPOINTS.PROMOTION.HISTORY(sopId));
		return response.data.data.promotions;
	},

	async stop(sopId: string): Promise<PromotionRecord | null> {
		const response = await api.post<ApiResponse<PromotionRecord | null>>(API_ENDPOINTS.PROMOTION.STOP(sopId));
		return response.data.data;
	},

	async getSettings(): Promise<PromotionSettings> {
		const response = await api.get<ApiResponse<PromotionSettings>>(API_ENDPOINTS.PROMOTION.SETTINGS);
		return response.data.data;
	},

	/** Sets thresholds of the account, or returns one to its default with null. */
	async updateSettings(change: PromotionSettingsChange): Promise<PromotionSettings> {
		const response = await api.put<ApiResponse<PromotionSettings>>(API_ENDPOINTS.PROMOTION.SETTINGS, change);
		return response.data.data;
	}
};
